import { CustomButton } from "@/components/custom/custom-button";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { showToast } from "@/redux/actions/toast-actions";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  styled,
  Stack,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { useProductCategories } from "@/hooks/use-products-categories";
import { changeUserCategory } from "@/services/pricing-config.service";
import * as Yup from "yup";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    padding: theme.spacing(2),
    maxWidth: 460,
    width: "100%",
  },
}));

const moveCategorySchema = Yup.object().shape({
  categoryId: Yup.number().required("Please select a category"),
});

const MoveCategoryDialog = ({ open, onClose, user }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: categoriesData } = useProductCategories(true);

  const categoryOptions = (categoriesData?.data?.data ?? [])
    .filter((cat) => cat.isActive && cat.id !== user?.category?.id)
    .map((cat) => ({
      label: `${cat.name} (${cat.code})`,
      value: cat.id,
    }));

  const formik = useFormik({
    initialValues: {
      categoryId: "",
    },
    enableReinitialize: true,
    validationSchema: moveCategorySchema,
    validateOnMount: true,
    onSubmit: (values) => {
      mutation.mutate({ categoryId: Number(values.categoryId) });
    },
  });

  const mutation = useMutation({
    mutationFn: (payload) =>
      changeUserCategory({ id: user?.userId, ...payload }),
    onSuccess: (data) => {
      formik.resetForm();
      dispatch(showToast(data?.message ?? "User category updated", "success"));
      queryClient.invalidateQueries({ queryKey: ["users-of-category"] });
      onClose();
    },
    onError: (err) => {
      dispatch(showToast(err?.message ?? "Something went wrong", "error"));
    },
  });

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    mutation.reset();
    onClose();
  };

  const userName =
    user?.user?.name || user?.user?.email || `User ${user?.userId}`;
  const currentCategory = user?.category?.name ?? "—";

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 2,
          pt: 2,
          pb: 0,
        }}
      >
        <Box>
          <DialogTitle
            sx={{
              p: 0,
              fontSize: "1.1rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            Move User to Another Category
          </DialogTitle>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Moving <strong>{userName}</strong> from{" "}
            <strong>{currentCategory}</strong>. Select the new category below.
          </Typography>
        </Box>

        <Stack spacing={2}>
          <FormSelectInput
            formik={formik}
            name="categoryId"
            label="New Category"
            options={categoryOptions}
          />
        </Stack>

        {mutation.isError && (
          <CustomErrorMessage
            message={mutation.error?.data?.[0] ?? "Failed. Try again."}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 2, pb: 1, gap: 1 }}>
        <CustomButton
          variantType="outline"
          size="small"
          onClick={handleClose}
          disabled={mutation.isPending}
          sx={{ px: 2 }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || !formik.dirty || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Moving..." : "Move User"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default MoveCategoryDialog;
